import type { ProgressItem } from '@/types';

interface ProgressCategoryCardProps {
  category: string;
  items: ProgressItem[];
  busy: boolean;
  busyId: string | null;
  onToggle: (item: ProgressItem) => void;
  onRemoveItem: (id: string) => void;
  onCompleteAll: (category: string) => void;
  onRemoveCategory: (category: string) => void;
}

export default function ProgressCategoryCard({
  category,
  items,
  busy,
  busyId,
  onToggle,
  onRemoveItem,
  onCompleteAll,
  onRemoveCategory,
}: ProgressCategoryCardProps) {
  const done = items.filter((i) => i.status === 'done').length;
  const total = items.length;
  const remaining = total - done;
  const percent = total === 0 ? 0 : Math.round((done / total) * 100);
  const allDone = total > 0 && done === total;

  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-secondary-100 text-secondary-800">
              <i className="ri-book-2-line"></i>
            </span>
            {category}
          </h3>
          <p className="mt-1 text-xs text-foreground-500">
            완료 {done}개 · 남은 진도 {remaining}개 / 전체 {total}개
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => onCompleteAll(category)}
            disabled={busy || allDone}
            className="flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-xs font-semibold text-foreground-700 transition hover:bg-background-100 disabled:opacity-40 whitespace-nowrap"
          >
            <i className="ri-check-double-line"></i>
            전체 완료
          </button>
          <button
            type="button"
            onClick={() => onRemoveCategory(category)}
            disabled={busy}
            className="flex items-center gap-1.5 rounded-md border border-accent-200 px-3 py-1.5 text-xs font-semibold text-accent-700 transition hover:bg-accent-50 disabled:opacity-50 whitespace-nowrap"
          >
            <i className="ri-delete-bin-6-line"></i>
            교육과정 삭제
          </button>
        </div>
      </div>

      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-background-200">
        <div
          className="h-full rounded-full bg-primary-500 transition-all"
          style={{ width: `${percent}%` }}
        />
      </div>

      <ul className="mt-4 flex flex-col gap-1.5">
        {items.map((item) => {
          const isDone = item.status === 'done';
          return (
            <li
              key={item.id}
              className="flex items-center gap-2 rounded-lg border border-background-200 bg-background-100 pr-2"
            >
              <button
                type="button"
                onClick={() => onToggle(item)}
                disabled={busyId === item.id}
                className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 text-left transition hover:bg-background-200/60 disabled:opacity-60"
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border text-[11px] ${
                    isDone
                      ? 'border-primary-500 bg-primary-500 text-background-50'
                      : 'border-background-300 bg-background-50 text-transparent'
                  }`}
                >
                  <i className="ri-check-line"></i>
                </span>
                <span
                  className={`min-w-0 flex-1 truncate text-sm ${
                    isDone ? 'font-medium text-foreground-950' : 'text-foreground-700'
                  }`}
                >
                  {item.unit}
                </span>
                {isDone ? (
                  <span className="flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-0.5 text-[11px] font-semibold text-primary-700 whitespace-nowrap">
                    완료
                  </span>
                ) : (
                  <span className="flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-0.5 text-[11px] font-semibold text-secondary-900 whitespace-nowrap">
                    예정
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => onRemoveItem(item.id)}
                disabled={busyId === item.id}
                aria-label="단원 삭제"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-foreground-400 transition hover:bg-background-200 hover:text-accent-700 disabled:opacity-50"
              >
                <i className="ri-close-line"></i>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}